import { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type EvaluatedRow = {
  index: number;
  raw: string;
  expression: string;
  variable?: string;
  value?: number;
  formatted?: string;
  error?: string;
  show: boolean;
};

export type MarkShowItem = {
  id: string;
  label: string;
  value: number;
};

// Represents a 3D object to visualize on the grid
export type MarkObject = {
  id: string;
  lineIndex: number;
  type: "box";
  position: [number, number, number];
  size: [number, number, number];
  color: string;
  label?: string;
};

// Represents a text label to show on the grid
export type MarkLabel = {
  id: string;
  lineIndex: number;
  text: string;
  // Position will be determined by nearest object above this line
  attachToObjectId?: string;
  value?: number;
};

export type MarkEvaluation = {
  unitLabel: string;
  gridSpacing: number; // The value of the first unit (grid spacing in that unit)
  unitErrors: string[];
  rows: EvaluatedRow[];
  showItems: MarkShowItem[];
  objects: MarkObject[];
  labels: MarkLabel[];
};

// Map of common color names to hex values
const COLOR_NAMES: Record<string, string> = {
  red: "#ef4444",
  green: "#22c55e",
  blue: "#3b82f6",
  yellow: "#eab308",
  orange: "#f97316",
  purple: "#a855f7",
  pink: "#ec4899",
  cyan: "#06b6d4",
  teal: "#14b8a6",
  lime: "#84cc16",
  indigo: "#6366f1",
  violet: "#8b5cf6",
  amber: "#f59e0b",
  emerald: "#10b981",
  rose: "#f43f5e",
  sky: "#0ea5e9",
  slate: "#64748b",
  gray: "#6b7280",
  grey: "#6b7280",
  white: "#ffffff",
  black: "#000000",
  brown: "#a16207",
  navy: "#1e3a5f",
  maroon: "#7f1d1d",
  olive: "#4d7c0f",
  coral: "#fb7185",
  salmon: "#fda4af",
  gold: "#ca8a04",
  silver: "#9ca3af",
  beige: "#d4c4a8",
  tan: "#d2b48c",
};

// Resolve a color value (name or hex)
function resolveColor(value: string): string {
  const lower = value.toLowerCase();
  return COLOR_NAMES[lower] ?? value;
}

const DEFAULT_UNIT_LABEL = "m";
const numberFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

const mathScope: Record<string, number | ((...args: number[]) => number)> = {
  PI: Math.PI,
  E: Math.E,
  abs: Math.abs,
  ceil: Math.ceil,
  floor: Math.floor,
  round: Math.round,
  sqrt: Math.sqrt,
  pow: Math.pow,
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  min: Math.min,
  max: Math.max,
};

const allowedExpr = /^[\d\s+/*()\-_.#,a-zA-Z=^]*$/;

function formatValue(value: number, unitLabel: string) {
  return `${numberFormat.format(value)} ${unitLabel}`;
}

function replaceUnits(expr: string, units: Record<string, number>) {
  return expr.replace(/(\d+(?:\.\d+)?)(\s*)([a-zA-Z][\w]*)/g, (_m, num, _ws, symbol) => {
    if (units[symbol] == null) return _m;
    return `(${num}*${units[symbol]})`;
  });
}

function stripLeadingComment(line: string): string {
  const trimmed = line.trim();
  if (trimmed.startsWith("//")) {
    return trimmed.slice(2).trimStart();
  }
  return trimmed;
}

function normalizeExpr(expr: string) {
  return expr.replace(/\^/g, "**");
}

function safeEval(expr: string, scope: Record<string, number>) {
  const keys = Object.keys({ ...mathScope, ...scope });
  const values = keys.map(k => (mathScope as any)[k] ?? scope[k]);
  try {
    const fn = new Function(...keys, `"use strict"; return (${expr});`);
    const result = fn(...values);
    if (typeof result !== "number" || Number.isNaN(result)) {
      throw new Error("Invalid result");
    }
    return result;
  } catch (err: any) {
    // Provide more helpful error message
    const msg = err?.message || "Evaluation failed";
    if (msg.includes("is not defined")) {
      const match = /(\w+) is not defined/.exec(msg);
      if (match) throw new Error(`${match[1]} is not defined`);
    }
    throw new Error(msg);
  }
}

// Parse a box definition like "box(width, depth, height)" or "box 3x3x2" or "box(w=3, d=3, h=2)"
function parseBoxDefinition(
  line: string,
  variables: Record<string, number>,
  units: Record<string, number>,
): { size: [number, number, number]; color?: string; label?: string; position?: [number, number, number] } | null {
  const trimmed = line.trim();
  
  // Match: box(w, d, h) or box(width, depth, height) with optional color and label
  const funcMatch = /^box\s*\(\s*([^)]+)\s*\)/i.exec(trimmed);
  if (funcMatch) {
    const args = funcMatch[1]!.split(",").map(a => a.trim());
    let w = 1, d = 1, h = 1;
    let color: string | undefined;
    let label: string | undefined;
    let position: [number, number, number] | undefined;
    
    for (let i = 0; i < args.length; i++) {
      const arg = args[i]!;
      const kvMatch = /^(\w+)\s*[:=]\s*(.+)$/.exec(arg);
      if (kvMatch) {
        const key = kvMatch[1]!.toLowerCase();
        const valStr = kvMatch[2]!;
        if (key === "color" || key === "c") {
          color = resolveColor(valStr.replace(/['"]/g, ""));
        } else if (key === "label" || key === "l" || key === "name") {
          label = valStr.replace(/['"]/g, "");
        } else if (key === "x" || key === "y" || key === "z") {
          const posVal = parseNumericValue(valStr, variables, units);
          if (posVal !== null) {
            if (!position) position = [0, 0, 0];
            if (key === "x") position[0] = posVal;
            else if (key === "y") position[1] = posVal;
            else if (key === "z") position[2] = posVal;
          }
        } else {
          const numVal = parseNumericValue(valStr, variables, units);
          if (numVal !== null) {
            if (key === "w" || key === "width") w = numVal;
            else if (key === "d" || key === "depth") d = numVal;
            else if (key === "h" || key === "height") h = numVal;
          }
        }
      } else {
        const numVal = parseNumericValue(arg, variables, units);
        if (numVal !== null) {
          if (i === 0) w = numVal;
          else if (i === 1) d = numVal;
          else if (i === 2) h = numVal;
        }
      }
    }
    return { size: [w, d, h], color, label, position };
  }
  
  // Match: box 3x3x2 or box 3 x 3 x 2
  const dimMatch = /^box\s+(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i.exec(trimmed);
  if (dimMatch) {
    return {
      size: [
        parseFloat(dimMatch[1]!),
        parseFloat(dimMatch[2]!),
        parseFloat(dimMatch[3]!),
      ],
    };
  }
  
  return null;
}

function parseNumericValue(
  expr: string,
  variables: Record<string, number>,
  units: Record<string, number>,
): number | null {
  try {
    const trimmed = expr.trim();
    // Check if it's a direct variable reference first
    if (variables[trimmed] !== undefined) {
      return variables[trimmed];
    }
    const prepared = normalizeExpr(replaceUnits(trimmed, units));
    if (!allowedExpr.test(prepared)) return null;
    const value = safeEval(prepared, variables);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

// Check if a line is a "label" line - plain text that should be visualized
function isLabelLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  // Skip lines that look like code/math
  if (/^[a-zA-Z_]\w*\s*=/.test(trimmed)) return false;
  if (/^box\s*[\(\d]/i.test(trimmed)) return false;
  if (/^\/\//.test(trimmed)) return false;
  if (/^#/.test(trimmed)) return false;
  // If it's mostly letters and spaces, treat as a label
  const alphaCount = (trimmed.match(/[a-zA-Z]/g) || []).length;
  return alphaCount > trimmed.length * 0.3 && trimmed.length > 0;
}

// Color palette for objects
const OBJECT_COLORS = [
  "#ef4444", "#f59e0b", "#10b981", "#3b82f6", 
  "#8b5cf6", "#ec4899", "#14b8a6", "#22c55e",
];

export function evaluateMarkLines(lines: string[]): MarkEvaluation {
  const unitLine = stripLeadingComment(lines[0] ?? "");
  const units: Record<string, number> = {};
  const unitErrors: string[] = [];
  let primaryUnit = DEFAULT_UNIT_LABEL;

  if (unitLine.length === 0) {
    units[primaryUnit] = 1;
  } else {
    const pieces = unitLine.split(",").map(p => p.trim()).filter(Boolean);
    if (pieces.length > 0) {
      pieces.forEach((piece, idx) => {
        const [lhsRaw, rhsRaw] = piece.split("=").map(p => p.trim());
        if (!lhsRaw) {
          unitErrors.push("Unit name missing");
          return;
        }
        if (idx === 0) primaryUnit = lhsRaw;
        if (!rhsRaw) {
          units[lhsRaw] = idx === 0 ? 1 : units[lhsRaw] ?? 1;
          return;
        }
        const candidateExpr = normalizeExpr(replaceUnits(rhsRaw, units));
        try {
          if (!allowedExpr.test(candidateExpr)) {
            throw new Error("Unsupported characters");
          }
          const value = safeEval(candidateExpr, units);
          units[lhsRaw] = value;
        } catch (error: any) {
          unitErrors.push(`${lhsRaw}: ${error?.message ?? "Failed to parse"}`);
        }
      });
    } else {
      units[primaryUnit] = 1;
    }
  }

  if (units[primaryUnit] == null) {
    units[primaryUnit] = 1;
  }

  const variables: Record<string, number> = { ...units };
  const rows: EvaluatedRow[] = [];
  const showItems: MarkShowItem[] = [];
  const objects: MarkObject[] = [];
  const labels: MarkLabel[] = [];
  let objectIndex = 0;
  let currentXOffset = 0;

  lines.slice(1).forEach((rawLine, idx) => {
    const raw = rawLine ?? "";
    const lineNumber = idx + 1;
    const show = /#show\b/i.test(raw);
    const visualize = /#vis\b/i.test(raw);
    const withoutDirectives = raw.replace(/\s*#show\b/gi, "").replace(/\s*#vis\b/gi, "").trim();
    
    // Skip empty lines
    if (withoutDirectives.length === 0) {
      rows.push({
        index: lineNumber,
        raw,
        expression: "",
        show,
      });
      return;
    }
    
    // Skip comment lines
    if (withoutDirectives.startsWith("//")) {
      rows.push({
        index: lineNumber,
        raw,
        expression: "",
        show,
      });
      return;
    }
    
    // Check for box definition
    const boxDef = parseBoxDefinition(withoutDirectives, variables, units);
    if (boxDef) {
      const objId = `obj-${lineNumber}`;
      const color = boxDef.color ?? OBJECT_COLORS[objectIndex % OBJECT_COLORS.length];
      const position = boxDef.position ?? [currentXOffset + boxDef.size[0] / 2, boxDef.size[1] / 2, 0];
      
      if (visualize || show) {
        objects.push({
          id: objId,
          lineIndex: lineNumber,
          type: "box",
          position: position as [number, number, number],
          size: boxDef.size,
          color: color!,
          label: boxDef.label,
        });
        currentXOffset += boxDef.size[0] + 1; // Space objects apart
      }
      
      objectIndex++;
      rows.push({
        index: lineNumber,
        raw,
        expression: withoutDirectives,
        formatted: `Box ${boxDef.size[0]}×${boxDef.size[2]}×${boxDef.size[1]} ${primaryUnit}`,
        show,
      });
      return;
    }
    
    // Check if it's a label line (plain text to visualize)
    if (isLabelLine(withoutDirectives) && (visualize || show)) {
      // Find the nearest object above this line to attach to
      const nearestObject = objects.length > 0 ? objects[objects.length - 1] : undefined;
      labels.push({
        id: `label-${lineNumber}`,
        lineIndex: lineNumber,
        text: withoutDirectives,
        attachToObjectId: nearestObject?.id,
      });
      rows.push({
        index: lineNumber,
        raw,
        expression: "",
        show,
      });
      return;
    }
    
    // Standard math evaluation
    const assignMatch = /^([a-zA-Z_][\w]*)\s*=\s*(.+)$/.exec(withoutDirectives);
    const variable = assignMatch?.[1];
    const expression = assignMatch ? assignMatch[2] ?? "" : withoutDirectives;
    const prepared = normalizeExpr(replaceUnits(expression, variables));
    const row: EvaluatedRow = {
      index: lineNumber,
      raw,
      expression,
      variable,
      show,
    };
    try {
      if (!allowedExpr.test(prepared)) {
        throw new Error("Unsupported characters");
      }
      const value = safeEval(prepared, variables);
      row.value = value;
      row.formatted = formatValue(value, primaryUnit);
      if (variable) {
        variables[variable] = value;
      }
      if (show) {
        showItems.push({
          id: `show-${lineNumber}`,
          label: `${variable ?? `line ${lineNumber}`} = ${row.formatted}`,
          value,
        });
      }
      // If #vis is used, add as a label with the computed value
      if (visualize) {
        const nearestObject = objects.length > 0 ? objects[objects.length - 1] : undefined;
        labels.push({
          id: `label-${lineNumber}`,
          lineIndex: lineNumber,
          text: variable ? `${variable} = ${row.formatted}` : row.formatted!,
          attachToObjectId: nearestObject?.id,
          value,
        });
      }
    } catch (error: any) {
      row.error = error?.message ?? "Could not evaluate";
    }
    rows.push(row);
  });

  // The grid spacing is the value of the primary unit (first defined unit)
  const gridSpacing = units[primaryUnit] ?? 1;
  
  return { unitLabel: primaryUnit, gridSpacing, unitErrors, rows, showItems, objects, labels };
}

type MarkPadProps = {
  lines: string[];
  evaluation: MarkEvaluation;
  onLinesChange: (lines: string[]) => void;
  onResetTemplate: () => void;
};

export function MarkPad({
  lines,
  evaluation,
  onLinesChange,
  onResetTemplate,
}: MarkPadProps) {
  const [showHelp, setShowHelp] = useState(false);
  const [resultsWidth, setResultsWidth] = useState(180);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const isDraggingResults = useRef(false);
  const startResultsX = useRef(0);
  const startResultsWidth = useRef(180);

  // Sync scroll between textarea, highlight layer, and results
  const handleScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
    if (resultsRef.current) {
      resultsRef.current.scrollTop = e.currentTarget.scrollTop;
    }
    if (highlightRef.current) {
      highlightRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newLines = e.target.value.split("\n");
    onLinesChange(newLines);
  }, [onLinesChange]);

  // Syntax highlighting for a single line
  const highlightLine = useCallback((line: string): React.ReactNode => {
    if (!line) return <span>&nbsp;</span>;
    
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;
    
    // Helper to add a highlighted span
    const addPart = (text: string, className: string) => {
      parts.push(<span key={key++} className={className}>{text}</span>);
    };
    
    // Check for comment
    if (remaining.trimStart().startsWith("//")) {
      const leadingSpaces = remaining.match(/^(\s*)/)?.[1] || "";
      addPart(leadingSpaces, "");
      addPart(remaining.slice(leadingSpaces.length), "text-muted-foreground italic");
      return parts;
    }
    
    // Process tokens
    const tokenRegex = /(#vis|#show)|(\b(?:box|min|max|sqrt|pow|sin|cos|tan|abs|ceil|floor|round)\b)|(\/\/.*$)|(\b[a-zA-Z_][a-zA-Z0-9_]*\b)|((?:\d+\.?\d*|\.\d+)(?:\s*(?:m|ft|cm|mm|in|yd))?)|([+\-*/^()=,])|(\s+)|(.)/g;
    
    let match;
    while ((match = tokenRegex.exec(remaining)) !== null) {
      const [fullMatch, directive, func, comment, identifier, number, operator, whitespace, other] = match;
      
      if (directive) {
        // #vis, #show tags
        addPart(fullMatch, "text-violet-500 dark:text-violet-400 font-medium");
      } else if (func) {
        // Function names like box, min, max, etc.
        addPart(fullMatch, "text-blue-500 dark:text-blue-400");
      } else if (comment) {
        // Comments
        addPart(fullMatch, "text-muted-foreground italic");
      } else if (identifier) {
        // Check if it's a known variable or unit
        const isUnit = ["m", "ft", "cm", "mm", "in", "yd"].includes(identifier);
        if (isUnit) {
          addPart(fullMatch, "text-teal-600 dark:text-teal-400");
        } else {
          // Variable name
          addPart(fullMatch, "text-amber-600 dark:text-amber-400");
        }
      } else if (number) {
        // Numbers (possibly with unit suffix)
        const numMatch = fullMatch.match(/^([\d.]+)(\s*)(\w*)$/);
        if (numMatch && numMatch[1]) {
          addPart(numMatch[1], ""); // Numbers as default text color
          if (numMatch[2]) addPart(numMatch[2], "");
          if (numMatch[3]) addPart(numMatch[3], "text-teal-600 dark:text-teal-400");
        } else {
          addPart(fullMatch, "");
        }
      } else if (operator) {
        // Operators
        addPart(fullMatch, "text-rose-500 dark:text-rose-400");
      } else if (whitespace) {
        addPart(fullMatch, "");
      } else if (other) {
        addPart(fullMatch, "");
      }
    }
    
    return parts.length > 0 ? parts : <span>{line}</span>;
  }, []);

  // Generate highlighted content
  const highlightedContent = useMemo(() => {
    return lines.map((line, idx) => (
      <div key={idx} className="h-6 whitespace-pre" style={{ lineHeight: "24px" }}>
        {highlightLine(line)}
      </div>
    ));
  }, [lines, highlightLine]);

  // Build display rows - units line + evaluated rows
  const displayRows = useMemo(() => {
    const result: Array<{
      lineNum: number;
      hasResult: boolean;
      result?: string;
      error?: string;
      isBox?: boolean;
      isLabel?: boolean;
    }> = [];
    
    // Line 0 is units
    result.push({
      lineNum: 0,
      hasResult: evaluation.unitErrors.length === 0 && Boolean(lines[0]?.trim()),
      result: evaluation.unitErrors.length === 0 ? `Unit: ${evaluation.unitLabel}` : undefined,
      error: evaluation.unitErrors.length > 0 ? evaluation.unitErrors.join(", ") : undefined,
    });
    
    // Rest are evaluated rows
    for (const row of evaluation.rows) {
      const isBox = /^box\s*[\(\d]/i.test(row.expression || row.raw);
      const isLabel = evaluation.labels.some(l => l.lineIndex === row.index);
      result.push({
        lineNum: row.index,
        hasResult: row.formatted !== undefined || isBox,
        result: row.formatted,
        error: row.error,
        isBox,
        isLabel,
      });
    }
    
    return result;
  }, [evaluation, lines]);

  const sourceText = lines.join("\n");

  // Handle results panel resize
  const handleResultsMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingResults.current = true;
    startResultsX.current = e.clientX;
    startResultsWidth.current = resultsWidth;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  }, [resultsWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingResults.current) return;
      const delta = startResultsX.current - e.clientX;
      const newWidth = Math.min(300, Math.max(100, startResultsWidth.current + delta));
      setResultsWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isDraggingResults.current) {
        isDraggingResults.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <Card className="pointer-events-auto border-muted/70 bg-background/90 shadow-xl backdrop-blur h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-muted/50">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold">Space Calculator</h2>
          <span className="text-xs text-muted-foreground">Unit: {evaluation.unitLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-xs"
            onClick={() => setShowHelp(!showHelp)}
          >
            {showHelp ? "Hide help" : "Help"}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-xs"
            onClick={onResetTemplate}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Help panel */}
      {showHelp && (
        <div className="px-4 py-3 border-b border-muted/50 bg-muted/20 text-xs space-y-2">
          <p className="font-medium">Quick Reference:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• <code className="bg-muted px-1 rounded">Line 1</code>: Set grid spacing &amp; units. <code>m = 1</code> means 1m grid. <code>m = 0.5</code> means 0.5m grid.</li>
            <li>• <code className="bg-muted px-1 rounded">variable = expression</code>: Create variables with math</li>
            <li>• <code className="bg-muted px-1 rounded">box(w, d, h)</code>: Define a 3D box object</li>
            <li>• <code className="bg-muted px-1 rounded">#vis</code>: Visualize the line on the grid</li>
            <li>• Plain text with <code>#vis</code>: Labels attached to nearest object above</li>
          </ul>
          <p className="text-muted-foreground">
            Example: <code>ft = 1, m = 3.28 ft</code> sets a 1ft grid
          </p>
        </div>
      )}

      {/* Main editor area - Soulver style split */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left: Editor with syntax highlighting */}
        <div className="flex-1 relative">
          {/* Syntax highlight layer (behind textarea) */}
          <div
            ref={highlightRef}
            className="absolute inset-0 w-full h-full overflow-hidden font-mono text-sm px-4 py-3 pointer-events-none"
            style={{ lineHeight: "24px", tabSize: 2 }}
            aria-hidden="true"
          >
            {highlightedContent}
          </div>
          {/* Transparent textarea on top for editing */}
          <textarea
            ref={textareaRef}
            value={sourceText}
            onChange={handleTextChange}
            onScroll={handleScroll}
            spellCheck={false}
            className="absolute inset-0 w-full h-full resize-none bg-transparent font-mono text-sm leading-6 px-4 py-3 focus:outline-none border-r border-muted/30 selection:bg-primary/30 caret-black dark:caret-white"
            style={{ 
              lineHeight: "24px",
              tabSize: 2,
              color: "transparent",
            }}
            placeholder=""
          />
        </div>

        {/* Resize handle for results panel */}
        <div
          className="w-2 cursor-ew-resize shrink-0 group relative"
          onMouseDown={handleResultsMouseDown}
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full bg-muted-foreground/20 group-hover:bg-muted-foreground/50 transition-colors" />
        </div>

        {/* Right: Results column */}
        <div 
          ref={resultsRef}
          className="overflow-y-auto bg-muted/10 border-l border-muted/30 shrink-0"
          style={{ 
            width: `${resultsWidth}px`,
            lineHeight: "24px",
          }}
        >
          <div className="py-3 px-3 font-mono text-sm">
            {displayRows.map((row, idx) => (
              <div 
                key={idx} 
                className="h-6 flex items-center justify-end"
              >
                {row.error ? (
                  <span className="text-destructive text-xs truncate" title={row.error}>
                    ⚠ {row.error}
                  </span>
                ) : row.hasResult ? (
                  <span 
                    className={`text-right truncate ${
                      row.isBox 
                        ? "text-primary font-medium" 
                        : row.isLabel 
                          ? "text-amber-500" 
                          : "text-emerald-500"
                    }`}
                    title={row.result}
                  >
                    {row.result}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer status */}
      <div className="px-4 py-2 border-t border-muted/50 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {evaluation.objects.length > 0 && (
            <span className="text-primary">{evaluation.objects.length} object{evaluation.objects.length !== 1 ? "s" : ""}</span>
          )}
          {evaluation.objects.length > 0 && evaluation.labels.length > 0 && " · "}
          {evaluation.labels.length > 0 && (
            <span className="text-amber-500">{evaluation.labels.length} label{evaluation.labels.length !== 1 ? "s" : ""}</span>
          )}
          {evaluation.objects.length === 0 && evaluation.labels.length === 0 && (
            "Add #vis to visualize"
          )}
        </span>
        <span>{lines.length} lines</span>
      </div>
    </Card>
  );
}
